import { motion } from 'framer-motion';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getGameTypeDetails } from '../../../constants/Games/gameType';
import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import { IconType, Icons } from '../../../constants/icons';
import { Images } from '../../../constants/images';
import { getPathBGByIndex } from '../../../constants/pathBackground';
import { SpaceType } from '../../../constants/space';
import { SVGIconType } from '../../../constants/svgIcon';
import { colorForGameSetId } from '../../../constants/Theme/DefaultColors';
import { Space } from '../../../models/SpaceStore';
import { Album, AlbumAudience } from '../../../models/SpaceStore/Album';
import { Profile } from '../../../models/UserStore';
import { timeAgo } from '../../../utils/date';
import { AvatarNode } from '../AvatarNode';
import { SeparatorComponent } from '../SeparatorComponent';
import { Skeleton } from '../Skeleton';
import { SvgIcon } from '../SVGIcon';
import './AlbumCell.css';

export type AlbumCellProps = {
  album: Album;
  albumClassroom: Space | undefined | null;
  classroomOwnerProfile?: Profile | undefined | null;
  onClick: () => void;
};

export const AlbumCell = memo((props: AlbumCellProps) => {
  const { album, albumClassroom, classroomOwnerProfile, onClick } = props;
  const { t, i18n } = useTranslation();

  const albumInfo =
    album?.i18nGameSetInfo.resources[album?.i18nGameSetInfo.defaultLocale];
  const gameTypeIcons = album?.content?.partIcons
    ? new Set(
        Object.values(album?.content?.partIcons)
          .filter((iconName) => iconName !== null)
          .map((iconName) =>
            iconName
              ? getGameTypeDetails(iconName).icon ?? IconType.GameLine
              : IconType.GameLine,
          ),
      )
    : undefined;
  const [isAlbumClassroomIconAvailable, setIsAlbumClassroomIconAvailable] =
    useState(true);

  const albumIcon = getPathBGByIndex(
    album?.content?.path[0]?.style.background.set ?? 0,
  );
  return (
    <div className="album-cell">
      <div className="centered-container">
        <motion.div
          className="cell-row"
          onClick={onClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 0.96 }}
          whileTap={{ scale: 0.96 }}
        >
          <div className="album-icon-component">
            <div className="album-icon-container">
              <SvgIcon
                name={SVGIconType.BubbleShape}
                fill={colorForGameSetId(album.gameSetId)}
                className="bubble-shape"
              />
              <>
                <img
                  src={
                    album.icons[0]?.publicUrls?.medium ??
                    albumIcon ??
                    Images.speakingGameBackground
                  }
                  className="album-icon"
                />
                <div className="album-title-container">
                  <p className="album-title">{albumInfo?.name}</p>
                </div>
              </>
              {/* <SvgIcon
                name={SVGIconType.Share}
                className={`share-icon`}
               
              /> */}
            </div>
            <div className="game-type-container">
              <div className="game-type-icon-container">
                {gameTypeIcons && gameTypeIcons?.size > 0 ? (
                  <div className="game-icon-container">
                    {Array.from(gameTypeIcons)
                      .slice(0, 4)
                      .map((iconType) => (
                        <img
                          key={iconType}
                          className="game-type-icon"
                          src={Icons[iconType]}
                          alt={iconType}
                        />
                      ))}
                  </div>
                ) : null}

                {album &&
                album.statistics.itemCount &&
                album.statistics.itemCount > 0 ? (
                  <div className="item-count-container">
                    <p className="item-count">{album.statistics.itemCount}</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="text-component">
            <p className="album-name">{albumInfo?.name}</p>
            <div className="info">
              <SeparatorComponent
                separator={<p className="separate-text">·</p>}
              >
                <div className="avatar-icon-container">
                  {classroomOwnerProfile &&
                  albumClassroom?.type == SpaceType.Home ? (
                    <>
                      <AvatarNode
                        avatar={{
                          ...classroomOwnerProfile.avatar,
                          userId: classroomOwnerProfile.userId,
                          source:
                            classroomOwnerProfile.profilePicture &&
                            classroomOwnerProfile.profilePicture.publicUrls
                              ?.small
                              ? {
                                  uri: classroomOwnerProfile.profilePicture
                                    .publicUrls.small,
                                }
                              : NOT_APPLICABLE_STRING,
                        }}
                        size={32}
                      />
                    </>
                  ) : albumClassroom?.icon?.publicUrls?.small &&
                    isAlbumClassroomIconAvailable ? (
                    <img
                      className="space-icon"
                      src={albumClassroom?.icon?.publicUrls?.small}
                      onError={() => setIsAlbumClassroomIconAvailable(false)}
                    />
                  ) : (
                    <Skeleton
                      width={'32px'}
                      height={'32px'}
                      borderRadius={'1rem'}
                    />
                  )}

                  <p className="small-grey-text">
                    {
                      albumClassroom?.i18nClassroomInfo.resources[
                        albumClassroom.i18nClassroomInfo.defaultLocale
                      ]?.name
                    }
                  </p>
                </div>
                <p className="small-grey-text">
                  {timeAgo(album.createdAt, i18n.language)}
                </p>

                <SvgIcon
                  className="privacy-icon"
                  name={
                    album.audience == AlbumAudience.Public
                      ? SVGIconType.Region
                      : album.audience == AlbumAudience.Members
                      ? SVGIconType.Member
                      : album.audience == AlbumAudience.Followers
                      ? SVGIconType.Friend
                      : SVGIconType.MembershipTier
                  }
                />

                <p className="small-grey-text">
                  {t('{count} Plays', {
                    count: album.statistics.gamePlayCount ?? 0,
                  })}
                </p>
              </SeparatorComponent>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
});
